/* eslint-disable no-restricted-imports */
/* eslint-disable complexity */
import { useCallback, useEffect, useRef } from 'react';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useDpFanFetcher, flattenLeaves } from '@/data/Content/DpFan/fetcher';
import {
	DP_FAN_STAGES,
	DpFanStagesStateSizes,
	DpFanStagesStateTeams,
	DpFanStagesStateSports,
	DpFanStagesStateSportGroups,
	DpFanAnyItem,
	DpFanSports,
	DpFanTeamData,
	DpFanTeams,
} from '@/data/Content/DpFan/types';
import { useNotifications } from '@/data/Content/Notifications';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { processError } from '@/data/utils/processError';
import { Switch } from '@/utils/switch';
import { useIsLoadingBarNextRoute } from 'integration/hooks/useIsLoadingNextRoute';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { parseUserPreferredSizes } from 'integration/hooks/usePreferredSizes';
import {
	generateDpFanSelectedSportsBody,
	generateDpFanUpdateBody,
	generateDpFanUpdateTeamsBody,
} from '@/data/Content/DpFan/utils';
import {
	getLocalStorageDpFanState,
	removeLocalStorageDpFanState,
	setLocalStorageDpFanState,
} from '@/data/Content/DpFan/localStorage';
import { useDpFanState } from '@/data/Content/DpFan/state';
import { useUpdatePreferredSizes } from 'integration/hooks/useUpdatePreferredSizes';
import { formatDateToDisplay } from 'integration/utils/convertDateToBackendFormat';

export { DP_FAN_STAGES };
export type {
	DpFanAnyItem,
	DpFanSports,
	DpFanTeamData,
	DpFanTeams,
	DpFanStagesStateSizes,
	DpFanStagesStateSports,
	DpFanStagesStateSportGroups,
	DpFanStagesStateTeams,
};

export const useDpFanStages = () => {
	const {
		dpFanState,
		onChangeSportState,
		onChangeTeamState,
		onChangeSizeState,
		onChangeBrandState,
		onChangeLeagueState,
		onChangeActiveSportState,
		onPrevStageState,
		onNextStageState,
		onStartSubmitState,
		onEndSubmitState,
		onPartialUpdateState,
	} = useDpFanState();

	const router = useNextRouter();
	const openAuthModal = useIsOpen();
	const { notifyError } = useNotifications();

	const { personInfo, isLoadingPersonInfo, onUpdatePreferredSizes } = useUpdatePreferredSizes();

	const {
		userBrandsData,
		interests,
		sports,
		teams,
		brands,
		sportGroups,
		userSportsData,
		preferredSizes,
		isLoadingPreferredSizes,
		isLoadingInterests,
		isLoadingUserInterests,
		isUserLoggedIn,
		onUpdateUserInterests,
	} = useDpFanFetcher();

	const { visible: isLoadingNextRoute } = useIsLoadingBarNextRoute({
		startDelayMs: 50,
		finishHoldMs: 500,
	});

	const onSyncDataUserInterests = useCallback(() => {
		const prevUserSports = userSportsData.map((item) => item.name);

		const prevUserTeams = userSportsData.reduce((acc, interest) => {
			const names = flattenLeaves(interest).map((item) => item.name);

			// Leaf categories (Casual, Relojes, …) have no teams; keep no empty
			// keys, mirroring how CHANGE_TEAM cleans them up.
			if (names.length > 0) {
				acc[interest.name] = names;
			}

			return acc;
		}, {} as DpFanStagesStateTeams);
		const prevSelectedGroups = userSportsData.reduce((acc, interest) => {
			const leagues = flattenLeaves(interest).reduce((groups, leaf) => {
				if (leaf.parentName !== interest.name && !groups.includes(leaf.parentName)) {
					groups.push(leaf.parentName);
				}
				return groups;
			}, [] as string[]);

			if (leagues.length > 0) {
				acc[interest.name] = leagues;
			}

			return acc;
		}, {} as DpFanStagesStateSportGroups);

		const prevUserBrands = userBrandsData?.children?.map((item) => item.name) ?? [];

		const userPreferredSizes = parseUserPreferredSizes(personInfo?.x_userProfileField1);

		let prevUserSizes = userPreferredSizes.reduce((acc, size) => {
			acc[size.label] = size.value;
			return acc;
		}, {} as DpFanStagesStateSizes);

		if (personInfo.gender) {
			prevUserSizes = {
				...prevUserSizes,
				GENDER: Switch(personInfo.gender)
					.case('Male', () => 'Masculino')
					.case('Female', () => 'Femenino')
					.defaultTo(() => 'Prefiero no decirlo'),
			};
		}

		if (personInfo.dateOfBirth) {
			prevUserSizes = {
				...prevUserSizes,
				BIRTHDAY: formatDateToDisplay(personInfo.dateOfBirth),
			};
		}

		return {
			sports: prevUserSports,
			teams: prevUserTeams,
			size: prevUserSizes,
			brands: prevUserBrands,
			sportGroups,
			selectedGroups: prevSelectedGroups,
		};
	}, [
		personInfo?.x_userProfileField1,
		personInfo?.gender,
		personInfo?.dateOfBirth,
		userSportsData,
		userBrandsData?.children,
		sportGroups,
	]);

	const onGetCurrentState = useCallback(() => {
		if (dpFanState.stage !== DP_FAN_STAGES.INITITAL) {
			return { ...dpFanState, sportGroups, needToSync: false };
		}

		const savedState = getLocalStorageDpFanState();

		if (savedState) {
			removeLocalStorageDpFanState();
			const parsed = JSON.parse(savedState);
			return {
				...parsed,
				sportGroups,
				needToSync: false,
				submitting: isUserLoggedIn ? parsed.submitting : false,
			};
		}

		return {
			...dpFanState,
			...onSyncDataUserInterests(),
			needToSync: false,
		};
	}, [dpFanState, onSyncDataUserInterests, sportGroups, isUserLoggedIn]);

	useEffect(() => {
		if (
			!isLoadingUserInterests &&
			!isLoadingInterests &&
			!isLoadingNextRoute &&
			!isLoadingPreferredSizes &&
			!isLoadingPersonInfo &&
			dpFanState.stage === DP_FAN_STAGES.INITITAL &&
			dpFanState.needToSync
		) {
			const newState = onGetCurrentState();
			onPartialUpdateState(newState);
		}
	}, [
		isLoadingPersonInfo,
		isLoadingUserInterests,
		isLoadingNextRoute,
		isLoadingPreferredSizes,
		isLoadingInterests,
		dpFanState.needToSync,
		dpFanState.stage,
		onGetCurrentState,
		onPartialUpdateState,
	]);

	const finishingRef = useRef(false);

	const onFinish = useCallback(async () => {
		if (finishingRef.current) {
			return;
		}
		finishingRef.current = true;

		onStartSubmitState();
		openAuthModal.off();

		const syncDpFanState = onGetCurrentState();

		try {
			const newUserInterests = generateDpFanUpdateBody({
				selectedSports: syncDpFanState.sports,
				selectedTeams: syncDpFanState.teams,
				selectedBrands: syncDpFanState.brands,
				sports,
				teams,
				brands,
			});

			await onUpdateUserInterests(newUserInterests);

			await onUpdatePreferredSizes(syncDpFanState.size);

			removeLocalStorageDpFanState();

			await router.replace('/dpfan-finalizado');
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));

			finishingRef.current = false;
			onPartialUpdateState({ ...syncDpFanState, submitting: false, needToSync: false });
		}
	}, [
		sports,
		teams,
		brands,
		onGetCurrentState,
		onUpdateUserInterests,
		notifyError,
		openAuthModal,
		router,
		onPartialUpdateState,
		onStartSubmitState,
		onUpdatePreferredSizes,
	]);

	const onNextStage = useCallback(async () => {
		if (dpFanState.stage === DP_FAN_STAGES.SIZES) {
			if (!isUserLoggedIn) {
				setLocalStorageDpFanState({ ...dpFanState, submitting: true });
				onStartSubmitState();
				openAuthModal.on();
				return;
			}

			await onFinish();
			return;
		}

		onNextStageState();
	}, [dpFanState, isUserLoggedIn, openAuthModal, onFinish, onNextStageState, onStartSubmitState]);

	useEffect(() => {
		if (
			isUserLoggedIn &&
			!dpFanState.needToSync &&
			dpFanState.submitting &&
			dpFanState.stage === DP_FAN_STAGES.SIZES
		) {
			void onFinish();
		}
	}, [isUserLoggedIn, dpFanState.needToSync, dpFanState.submitting, dpFanState.stage, onFinish]);

	const onUpdateUserSizes = useCallback(async () => {
		onStartSubmitState();

		try {
			await onUpdatePreferredSizes(dpFanState.size);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			onEndSubmitState();
		}
	}, [dpFanState.size, notifyError, onStartSubmitState, onEndSubmitState, onUpdatePreferredSizes]);

	const onRemoveUserSport = useCallback(
		async (sportId: number) => {
			const sport = sports.find((sport) => sport.id === sportId);

			if (!sport) return;

			onStartSubmitState();

			try {
				// Deselect the sport from Zona Fan and clear every league under it.
				const remainingSports = dpFanState.sports.filter((name) => name !== sport.name);

				await onUpdateUserInterests([
					...generateDpFanSelectedSportsBody(remainingSports, sports),
					...generateDpFanUpdateTeamsBody({}, [sport], teams),
				]);

				onChangeSportState(sport.name);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			} finally {
				onEndSubmitState();
			}
		},
		[
			sports,
			teams,
			dpFanState.sports,
			onUpdateUserInterests,
			onChangeSportState,
			notifyError,
			onStartSubmitState,
			onEndSubmitState,
		]
	);

	const onRemoveActiveSport = useCallback(() => {
		onPartialUpdateState({ ...onSyncDataUserInterests(), activeCategory: undefined });
	}, [onSyncDataUserInterests, onPartialUpdateState]);

	const onUpdateUserTeams = useCallback(async () => {
		onStartSubmitState();

		try {
			// Keep the Zona Fan sport selection in sync: clearing every team of a
			// sport also deselects it (CHANGE_TEAM already drops it from state).
			await onUpdateUserInterests([
				...generateDpFanSelectedSportsBody(dpFanState.sports, sports),
				...generateDpFanUpdateTeamsBody(dpFanState.teams, sports, teams),
			]);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			onPartialUpdateState({ submitting: false, activeCategory: undefined });
		}
	}, [
		dpFanState.teams,
		dpFanState.sports,
		onUpdateUserInterests,
		sports,
		teams,
		notifyError,
		onStartSubmitState,
		onPartialUpdateState,
	]);

	const onUpdateUserBrands = useCallback(async () => {
		onStartSubmitState();

		try {
			const newUserInterests = generateDpFanUpdateBody({
				selectedSports: dpFanState.sports,
				selectedTeams: dpFanState.teams,
				selectedBrands: dpFanState.brands,
				sports,
				teams,
				brands,
			});

			await onUpdateUserInterests(newUserInterests);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			onPartialUpdateState({ submitting: false, activeCategory: undefined });
		}
	}, [
		dpFanState.teams,
		dpFanState.sports,
		dpFanState.brands,
		onUpdateUserInterests,
		sports,
		teams,
		brands,
		notifyError,
		onStartSubmitState,
		onPartialUpdateState,
	]);

	const onChangeLeague = useCallback(
		(category: string, league: string) => {
			const leagueTeamNames = (teams[category] ?? [])
				.filter((team) => team.parentName === league)
				.map((team) => team.name);

			onChangeLeagueState(category, league, leagueTeamNames);
		},
		[teams, onChangeLeagueState]
	);

	const onCloseAuthModal = useCallback(() => {
		removeLocalStorageDpFanState();
		onEndSubmitState();
		openAuthModal.off();
	}, [openAuthModal, onEndSubmitState]);

	const skipBirthday = !!personInfo?.dateOfBirth;

	return {
		dpFanState,
		isOpenAuthModal: openAuthModal.open,
		preferredSizes,
		interests,
		sports,
		teams,
		brands,
		sportGroups,
		personInfo,
		skipBirthday,
		isLoadingUserInterests,
		isLoadingInterests,
		isLoadingNextRoute,
		isLoadingPreferredSizes,
		isLoadingPersonInfo,
		onChangeSize: onChangeSizeState,
		onCloseAuthModal,
		onChangeSport: onChangeSportState,
		onChangeBrand: onChangeBrandState,
		onChangeTeam: onChangeTeamState,
		onChangeLeague,
		onPrevStage: onPrevStageState,
		onNextStage,
		onFinish,
		onUpdateUserSizes,
		onRemoveUserSport,
		onChangeActiveSport: onChangeActiveSportState,
		onRemoveActiveSport,
		onUpdateUserTeams,
		onSyncDataUserInterests,
		onUpdateUserBrands,
	};
};
